'use client';

import { useState, useCallback, useRef } from 'react';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import type { ThroughputPoint, LatencyPoint, RegionHealth } from './useDashboardRealtime';

export interface AnomalyFlag {
  id: string;
  type: 'throughput' | 'latency' | 'sla';
  severity: 'warning' | 'critical';
  title: string;
  description: string;
  metric: string;
  recommendation: string;
}

export interface SLAPrediction {
  region: string;
  riskLevel: 'low' | 'medium' | 'high';
  estimatedBreachIn: string;
  affectedMetric: string;
  confidence: number;
}

export interface AnomalyAnalysis {
  anomalies: AnomalyFlag[];
  slaPredictions: SLAPrediction[];
  summary: string;
  analyzedAt: Date;
}

interface UseAnomalyDetectionReturn {
  analysis: AnomalyAnalysis | null;
  isAnalyzing: boolean;
  error: string | null;
  runAnalysis: (
    throughput: ThroughputPoint[],
    latency: LatencyPoint[],
    regions: RegionHealth[]
  ) => Promise<void>;
}

const SYSTEM_PROMPT = `You are a network operations AI analyst specializing in anomaly detection and SLA breach prediction. 
Analyze the provided telemetry data and return a JSON response with this exact structure:
{
  "anomalies": [
    {
      "id": "unique-id",
      "type": "throughput|latency|sla",
      "severity": "warning|critical",
      "title": "short title",
      "description": "what is unusual about this pattern",
      "metric": "the specific metric value that triggered this",
      "recommendation": "concise action to take"
    }
  ],
  "slaPredictions": [
    {
      "region": "region name",
      "riskLevel": "low|medium|high",
      "estimatedBreachIn": "e.g. ~2h or <30min",
      "affectedMetric": "latency p99 or throughput",
      "confidence": 0-100
    }
  ],
  "summary": "1-2 sentence executive summary of network health"
}
Only flag genuine anomalies. If data looks normal, return empty arrays. Be concise and actionable.`;

export function useAnomalyDetection(): UseAnomalyDetectionReturn {
  const [analysis, setAnalysis] = useState<AnomalyAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRunRef = useRef<number>(0);
  const COOLDOWN_MS = 120_000; // 2-minute cooldown between analyses to avoid rate limits

  const runAnalysis = useCallback(async (
    throughput: ThroughputPoint[],
    latency: LatencyPoint[],
    regions: RegionHealth[]
  ) => {
    if (throughput.length === 0 && latency.length === 0) return;

    const now = Date.now();
    if (now - lastRunRef.current < COOLDOWN_MS) {
      const remaining = Math.ceil((COOLDOWN_MS - (now - lastRunRef.current)) / 1000);
      setError(`Please wait ${remaining}s before re-analyzing to avoid rate limits.`);
      return;
    }
    lastRunRef.current = now;

    setIsAnalyzing(true);
    setError(null);

    try {
      // Truncate to last 5 points to reduce token usage and avoid rate limits
      const throughputSummary = throughput.slice(-5).map(p => ({
        time: p.time_label,
        in: Math.round(p.inbound * 10) / 10,
        out: Math.round(p.outbound * 10) / 10,
      }));

      const latencySummary = latency.slice(-5).map(p => ({
        time: p.time_label,
        p50: p.p50,
        p95: p.p95,
        p99: p.p99,
      }));

      const regionSummary = regions.map(r => ({
        name: r.name,
        score: r.score,
        incidents: r.incidents,
        latency: r.latency,
        throughput: r.throughput,
        critical: r.critical_nodes,
        degraded: r.degraded_nodes,
      }));

      const userMessage = `Analyze this network telemetry for anomalies and SLA breach risks:

THROUGHPUT (Gbps, recent): ${JSON.stringify(throughputSummary)}
LATENCY (ms, recent): ${JSON.stringify(latencySummary)}
REGIONS: ${JSON.stringify(regionSummary)}

SLA thresholds: latency p99 < 50ms, throughput inbound > 2 Gbps, region score > 70.
Flag genuine anomalies only. Be concise.`;

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: userMessage },
        ],
        { max_completion_tokens: 500 }
      );

      const content = response?.choices?.[0]?.message?.content ?? '';

      // Extract JSON from the response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid AI response format');

      const parsed = JSON.parse(jsonMatch[0]);

      setAnalysis({
        anomalies: parsed.anomalies ?? [],
        slaPredictions: parsed.slaPredictions ?? [],
        summary: parsed.summary ?? '',
        analyzedAt: new Date(),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Analysis failed';
      // Provide a user-friendly message for rate limit / quota errors
      if (msg.includes('429') || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('quota')) {
        setError('OpenAI quota exceeded. Running local rule-based analysis instead.');
        // Fallback: rule-based anomaly detection without AI
        const fallbackAnomalies: AnomalyFlag[] = [];
        const fallbackPredictions: SLAPrediction[] = [];

        // Check throughput anomalies
        throughput.slice(-5).forEach((p) => {
          if (p.inbound < 2) {
            fallbackAnomalies.push({
              id: `thr-${p.time_label}`,
              type: 'throughput',
              severity: p.inbound < 1 ? 'critical' : 'warning',
              title: 'Low Inbound Throughput',
              description: `Inbound throughput dropped to ${p.inbound.toFixed(1)} Gbps at ${p.time_label}`,
              metric: `${p.inbound.toFixed(1)} Gbps`,
              recommendation: 'Check upstream links and BGP peering sessions.',
            });
          }
        });

        // Check latency anomalies
        latency.slice(-5).forEach((p) => {
          if (p.p99 > 50) {
            fallbackAnomalies.push({
              id: `lat-${p.time_label}`,
              type: 'latency',
              severity: p.p99 > 100 ? 'critical' : 'warning',
              title: 'High P99 Latency',
              description: `P99 latency reached ${p.p99}ms at ${p.time_label}, exceeding 50ms SLA threshold`,
              metric: `${p.p99}ms p99`,
              recommendation: 'Investigate congestion on core routing paths.',
            });
          }
        });

        // Check region health
        regions.forEach((r) => {
          const risk: 'low' | 'medium' | 'high' = r.score < 60 ? 'high' : r.score < 75 ? 'medium' : 'low';
          if (risk !== 'low') {
            fallbackPredictions.push({
              region: r.name,
              riskLevel: risk,
              estimatedBreachIn: risk === 'high' ? '<30min' : '~2h',
              affectedMetric: r.latency > 50 ? 'latency p99' : 'throughput',
              confidence: risk === 'high' ? 80 : 60,
            });
          }
        });

        setAnalysis({
          anomalies: fallbackAnomalies,
          slaPredictions: fallbackPredictions,
          summary: `Rule-based analysis (AI unavailable): ${fallbackAnomalies.length} anomaly(ies) detected across ${fallbackPredictions.length} region(s) at risk.`,
          analyzedAt: new Date(),
        });
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  return { analysis, isAnalyzing, error, runAnalysis };
}
